// Third-party
import React from 'react';
import { connect } from 'react-redux';
import debounce from 'lodash/debounce';
import union from 'lodash/union';
import { SortingRule } from 'react-table';

// Components
import ContactPortfoliosList from '../modal-content/ContactPortfoliosList';
import { SecondaryButton, PrimaryButton, CloseButton } from '../../buttons';
import { ModelsPortfoliosList } from '../modal-content';
import { ContactsTable } from '../../tables';
import Modal from '@/views/components/modals/AccessibleModal';

// Utils
import heapUtils, { HEAP_CUSTOM_EVENTS } from '@/utils/3rd-party/heap';
import { FormattedMessage, formatMessage } from '@/utils/i18n';
import { userHasPermissionEnabled, PERMISSIONS } from '@/utils/permissions';
import { isEnterpriseModel } from '@/utils/portfolio/utils';

// Actions
import { ReduxState } from '../../../../redux/modules/types';
import { getMyContacts, GET_CONTACTS, setCurrentContact, setPersistenceSource } from '../../../../redux/modules/contacts';
import { selectIsLoadingEvery, startLoaderAction } from '../../../../redux/modules/ui/loaders';
import { setIsActiveContactPostMessage, setIsActivePortfolioPostMessage } from '@/redux/modules/post-message';

// Constants and types
import { SortingField, SortOrder } from '../../../../types/data';
import { PORTFOLIO_METRICS_FIELDS, PORTFOLIO_TABLE_COLUMNS } from '../../../../enums/Portfolio';
import GenericContact from '@/types/generic-contact';
import Portfolio from '@/types/portfolio';
import EnterpriseDetails from '@/types/enterprise-detail-info';
import User from '@/types/user';
import AppMode from '@/types/app-mode';
import { MODAL_SIZES } from '@/utils/constants';
import UserPreferences from '@/types/user-preferences';
import ThemeMode from '@/types/theme-mode';
import { ContactsTableFields } from '../../tables/ContactsTable';
import ContactPortfoliosModal from './ContactPortfoliosModal';
import { getContactLayout } from '@/utils/contacts';
import LoadModelsModal from './LoadModelsModal';
import { SEARCH_DEBOUNCE_TIME } from '@/utils/debounce/constants';

type StateProps = {
  contacts: GenericContact[];
  nextContactsToken?: string;
  loadingContacts: boolean;
  loadingContactPortfolios: boolean;
  userPreferences?: UserPreferences;
  isPostMessageActive: boolean;
};

type DispatchProps = {
  getMyContacts: (...args: Parameters<typeof getMyContacts>) => Promise<GenericContact[]>;
  startLoaderAction: (name: string) => void;
  setCurrentContact: (contact: GenericContact) => void;
  setIsActivePortfolioPostMessage: (isActive: boolean) => void;
  setIsActiveContactPostMessage: (isActive: boolean) => void;
  setPersistenceSource: (user: User, key: string, value?: string) => void;
};

type Props = StateProps &
  DispatchProps & {
    queries: {
      portfolios: string;
      contactPortfolios: string;
    };
    show: boolean;
    modelPortfolios: Portfolio[];
    myModelsPortfolios: Portfolio[];
    enterpriseModelsPortfolios: Portfolio[];
    enterprise: EnterpriseDetails;
    loadingModels: boolean;
    hasMoreModelPortfolios: boolean;
    hasMoreMyModelPortfolios: boolean;
    hasMoreEnterprisePortfolios: boolean;
    user: User;
    selectedContact?: {
      contact: GenericContact;
      portfolios?: Portfolio[];
      hasMore: {
        portfolios: boolean;
      };
    };
    isComparing: boolean;
    selectedTab: LOAD_PORTFOLIOS_TABS;
    loadContactPortfolios: (contact: GenericContact) => Promise<void>;
    onLoadMoreModelPortfolios: () => void;
    onLoadMoreMyModelsPortfolios: () => void;
    onLoadMoreEnterprisePortfolios: () => void;
    onLoadMoreContactPortfolios: (sort?: SortingField[]) => void;
    onSearchPortfolios: (query: string) => void;
    onSearchContactPortfolios: (query: string, sort?: SortingField[], shouldFetchNext?: boolean) => void;
    onClose: () => void;
    resetCurrentContact: () => void;
    addPortfoliosToComparison: (portfolios: Portfolio[]) => Promise<void>;
    onSort: (sorting: SortingRule[]) => void;
  };

type State = {
  selectedPortfolios: Portfolio[];
  contactsSearchQuery: string;
  contactsSortingFields: SortingField[];
  contactPortfoliosSortingFields: SortingField[];
  contactPortfoliosSearchQuery: string;
  selectedLinkedContact?: GenericContact;
  initialSelectedContact?: GenericContact;
};

export enum LOAD_PORTFOLIOS_TABS {
  MODELS = 'models',
  CONTACTS = 'contacts',
}

class LoadPortfoliosModal extends React.Component<Props, State> {
  readonly state: State = {
    selectedPortfolios: [],
    contactsSortingFields: [],
    contactsSearchQuery: '',
    contactPortfoliosSortingFields: [],
    contactPortfoliosSearchQuery: '',
  };

  static defaultProps = {
    selectedTab: LOAD_PORTFOLIOS_TABS.MODELS,
  };

  debouncedSearchContacts = debounce(() => {
    const { contactsSearchQuery, contactsSortingFields } = this.state;
    this.props.getMyContacts({ query: contactsSearchQuery, nextToken: undefined, sort: contactsSortingFields });
  }, SEARCH_DEBOUNCE_TIME);

  componentDidMount() {
    const { contacts, selectedContact, user } = this.props;
    this.setState({ initialSelectedContact: selectedContact?.contact });
    this.props.getMyContacts().then(() => {
      if (user.appConfig.mode === AppMode.CONTACT && contacts.length === 1) {
        this.onSelectContact(contacts[0]);
      }
    });
  }

  onUnselectPortfolios = (portfolios: Portfolio[]) => {
    this.setState(prevState => {
      const { selectedPortfolios } = prevState;
      const newPortfolios = selectedPortfolios.filter(p => !portfolios.includes(p));

      return { selectedPortfolios: newPortfolios };
    });
  };

  onSelectPortfolios = (portfolios: Portfolio[]) => {
    const { selectedPortfolios } = this.state;
    const newPortfolios = selectedPortfolios.slice();

    this.setState({ selectedPortfolios: union(newPortfolios, portfolios) });
  };

  handleSelectionChange = (selectedPortfolios: Portfolio[]) => {
    this.setState({ selectedPortfolios });
  };

  onLoadMoreContactPortfolios = () => {
    const { contactPortfoliosSortingFields } = this.state;

    this.props.onLoadMoreContactPortfolios(contactPortfoliosSortingFields);
  };

  onContactPortfoliosSearchChange = (query: string) => {
    const { contactPortfoliosSortingFields } = this.state;
    this.props.onSearchContactPortfolios(query, contactPortfoliosSortingFields, false);
  };

  onContactPortfoliosSortingChange = (sortingRules: SortingRule[]) => {
    const { queries } = this.props;
    const contactPortfoliosSortingFields = sortingRules.map(rule => ({
      id: rule.id,
      order: rule.desc ? SortOrder.Desc : SortOrder.Asc,
    }));

    this.setState({ contactPortfoliosSortingFields });
    this.props.onSearchContactPortfolios(queries.contactPortfolios, contactPortfoliosSortingFields, false);
  };

  clearSearch = () => {
    const { selectedTab } = this.props;

    if (selectedTab === LOAD_PORTFOLIOS_TABS.MODELS) {
      this.props.onSearchPortfolios('');
    }

    if (selectedTab === LOAD_PORTFOLIOS_TABS.CONTACTS) {
      this.props.onSearchContactPortfolios('');
    }
  };

  getDisplayedColumns = () => {
    const { user } = this.props;

    return [
      {
        key: PORTFOLIO_TABLE_COLUMNS.NAME,
      },
      {
        key: PORTFOLIO_TABLE_COLUMNS.RISK_PROFILE,
      },
      {
        key: PORTFOLIO_TABLE_COLUMNS.SHOW_DETAILS,
        hidden: !userHasPermissionEnabled(user, PERMISSIONS.SHOW_PORTFOLIO_DETAILS),
      },
    ]
      .filter(col => !col.hidden)
      .map(col => col.key);
  };

  getTabContent = () => {
    const {
      queries,
      modelPortfolios,
      myModelsPortfolios,
      enterpriseModelsPortfolios,
      hasMoreModelPortfolios,
      hasMoreMyModelPortfolios,
      hasMoreEnterprisePortfolios,
      selectedContact,
      contacts,
      user,
      loadingModels,
      enterprise,
      loadingContacts,
      loadingContactPortfolios,
      selectedTab,
      userPreferences,
      isPostMessageActive,
      setIsActivePortfolioPostMessage,
      setIsActiveContactPostMessage,
      setPersistenceSource,
    } = this.props;

    const { selectedPortfolios, contactsSearchQuery, selectedLinkedContact } = this.state;

    const displayedColumns = this.getDisplayedColumns();
    const displayedMetrics = [
      PORTFOLIO_METRICS_FIELDS.MER,
      PORTFOLIO_METRICS_FIELDS.YEAR_ONE,
      PORTFOLIO_METRICS_FIELDS.YEAR_THREE,
      PORTFOLIO_METRICS_FIELDS.SIZE,
    ];
    const isThemeLight = userPreferences?.theme === ThemeMode.LIGHT;
    switch (selectedTab) {
      case LOAD_PORTFOLIOS_TABS.MODELS: {
        return (
          <ModelsPortfoliosList
            onSort={this.props.onSort}
            query={queries.portfolios}
            portfolios={modelPortfolios}
            myPortfolios={myModelsPortfolios}
            enterprisePortfolios={enterpriseModelsPortfolios}
            enterprise={enterprise}
            selectedPortfolios={selectedPortfolios}
            loading={loadingModels}
            hasMorePortfolios={hasMoreModelPortfolios}
            hasMoreMyPortfolios={hasMoreMyModelPortfolios}
            resetSelectedPortfolios={() => this.setState({ selectedPortfolios: [] })}
            hasMoreEnterprisePortfolios={hasMoreEnterprisePortfolios}
            loadMorePortfolios={this.props.onLoadMoreModelPortfolios}
            loadMoreMyPortfolios={this.props.onLoadMoreMyModelsPortfolios}
            loadMoreEnterprisePortfolios={this.props.onLoadMoreEnterprisePortfolios}
            onSearchPortfolios={this.props.onSearchPortfolios}
            emptyStateHeader={formatMessage('EmptyState.NoModelPortfolios.Title')}
            displayEnterpriseLabels
            hideStats={false}
            displayedColumns={displayedColumns}
            onPortfoliosSelected={this.onSelectPortfolios}
            onUnselectPortfolios={this.onUnselectPortfolios}
            displayedMetrics={displayedMetrics}
            selectedTitle="PortfoliosTable.SelectedTitle.Models"
            user={user}
            isThemeLight={isThemeLight}
          />
        );
      }
      case LOAD_PORTFOLIOS_TABS.CONTACTS: {
        return (
          <>
            {(!selectedContact || (user.appConfig.mode === AppMode.CONTACT && selectedContact && !selectedLinkedContact)) && (
              <ContactsTable
                selectable={false}
                className="flex-fill"
                contacts={contacts}
                title={formatMessage('LoadPortfoliosModal.SelectContact')}
                user={user}
                data-private
                data-dd-privacy="mask"
                loading={loadingContacts}
                searchQuery={contactsSearchQuery}
                onClick={this.onSelectContact}
                onLoadMore={this.onLoadMoreContacts}
                onSortingChange={this.onContactsSortingChange}
                onContactsSearch={this.onContactsSearchChange}
                debouncedSearchContacts={this.debouncedSearchContacts}
                hasMore={this.props.nextContactsToken != null}
                isThemeLight={isThemeLight}
                orderedFields={[ContactsTableFields.Fname, ContactsTableFields.Lname, ContactsTableFields.Email]}
                isPostMessageActive={isPostMessageActive}
                setPersistenceSource={setPersistenceSource}
                setIsActiveContactPostMessage={setIsActiveContactPostMessage}
              />
            )}

            {((user.appConfig.mode !== AppMode.CONTACT && selectedContact) ||
              (user.appConfig.mode === AppMode.CONTACT && selectedContact && selectedLinkedContact)) && (
              <ContactPortfoliosList
                user={user}
                backButtonText="LoadPortfoliosModal.BackToContacts"
                emptyStateHeader={formatMessage('EmptyState.NoContactPortfolios.ContactTitle')}
                isLoading={loadingContactPortfolios}
                searchQuery={queries.contactPortfolios}
                selectedContact={selectedContact}
                onBack={this.handleBackToContacts}
                onLoadMorePortfolios={this.onLoadMoreContactPortfolios}
                onSearchPortfolios={this.onContactPortfoliosSearchChange}
                onSelectPortfolios={this.onSelectPortfolios}
                onSortPortfolios={this.onContactPortfoliosSortingChange}
                onUnselectPortfolios={this.onUnselectPortfolios}
                isThemeLight={isThemeLight}
                setPersistenceSource={setPersistenceSource}
                onRowSelectionChange={this.handleSelectionChange}
                isPostMessageActive={isPostMessageActive}
                setIsActivePortfolioPostMessage={setIsActivePortfolioPostMessage}
              />
            )}
          </>
        );
      }
      default: {
        return null;
      }
    }
  };

  onSelectContact = (contact: GenericContact) => {
    this.setState({ selectedLinkedContact: contact });
    this.props.loadContactPortfolios(contact);
  };

  clearContactState = () => {
    this.clearSearch();
    this.props.resetCurrentContact();
    this.setState({ selectedPortfolios: [] });
  };

  handleBackToContacts = () => {
    const { user } = this.props;
    if (user.appConfig.mode === AppMode.CONTACT) {
      this.setState({ selectedLinkedContact: undefined });
    }
    this.clearContactState();
  };

  onSelectPortfolio = (portfolio: Portfolio) => {
    const { selectedPortfolios } = this.state;
    let newPortfolios = selectedPortfolios.slice();

    const isSelected = selectedPortfolios.find(p => p._id === portfolio._id);

    if (isSelected) {
      newPortfolios = newPortfolios.filter(p => p._id !== portfolio._id);
    } else {
      newPortfolios.push(portfolio);
    }

    this.setState({ selectedPortfolios: newPortfolios });
  };

  onClose = () => {
    this.props.onClose();
    this.clearSearch();
    const { user } = this.props;
    // For IG we need to make sure the selectedContact stays the same
    if (user.appConfig.mode === AppMode.CONTACT && this.state.initialSelectedContact) {
      this.props.setCurrentContact(this.state.initialSelectedContact);
    }
  };

  onLoadSelectedPortfoliosCapIntel = (selectedPortfolios: Portfolio[]) => {
    selectedPortfolios.forEach(selectedPortfolio => {
      if (selectedPortfolio.mine || selectedPortfolio.enterprise) {
        heapUtils.track(HEAP_CUSTOM_EVENTS.LOAD_MODEL, { isEnterpriseModel: isEnterpriseModel(selectedPortfolio) });
      }
    });

    this.props.addPortfoliosToComparison(selectedPortfolios).then(() => {
      const { user } = this.props;
      if (user.appConfig.mode === AppMode.CONTACT && this.state.initialSelectedContact) {
        // Reset the current contact for IG
        this.props.setCurrentContact(this.state.initialSelectedContact);
      }
      this.onClose();
    });
  };

  onLoadSelectedPortfolios = async () => {
    const { selectedPortfolios } = this.state;

    this.onLoadSelectedPortfoliosCapIntel(selectedPortfolios);
  };

  handleSelectedPortfolios = async (selectedPortfolios: Portfolio[]) => {
    this.onLoadSelectedPortfoliosCapIntel(selectedPortfolios);
  };

  onContactsSearchChange = (contactsSearchQuery: string) => {
    this.setState({ contactsSearchQuery });
    this.props.startLoaderAction(GET_CONTACTS.type);

    this.debouncedSearchContacts();
  };

  onContactsSortingChange = (contactsSortingFields: SortingField[]) => {
    const { contactsSearchQuery } = this.state;

    this.setState({ contactsSortingFields });
    this.props.getMyContacts({ query: contactsSearchQuery, nextToken: undefined, sort: contactsSortingFields });
  };

  onLoadMoreContacts = () => {
    const { contactsSearchQuery, contactsSortingFields } = this.state;
    const { nextContactsToken } = this.props;

    this.props.getMyContacts({ query: contactsSearchQuery, nextToken: nextContactsToken, sort: contactsSortingFields });
  };

  renderModalV1 = (): JSX.Element => {
    const { show, isComparing, selectedTab } = this.props;
    const { selectedPortfolios } = this.state;

    return (
      <Modal show={show} className="load-models-modal" onHide={this.onClose} dialogClassName="modal-dialog-centered" size={MODAL_SIZES.LG}>
        <Modal.Body className="d-flex flex-column">
          {selectedTab === LOAD_PORTFOLIOS_TABS.MODELS && (
            <div className="modal-header">
              <FormattedMessage tagName="h1" id="ModelPortfoliosModal.Title" />
              <CloseButton onClick={this.onClose} />
            </div>
          )}

          <div id={`tabpanel-${selectedTab}`} className="content d-flex flex-column justify-content-center flex-grow-1" role="tabpanel" tabIndex={0}>
            {this.getTabContent()}
          </div>

          <div className="modal-actions d-flex justify-content-between">
            <SecondaryButton className="cancel-button" onClick={this.onClose} disabled={isComparing}>
              <FormattedMessage id="Common.Close" />
            </SecondaryButton>

            <PrimaryButton loading={isComparing} disabled={!selectedPortfolios.length} className="compare-button" onClick={this.onLoadSelectedPortfolios}>
              <FormattedMessage
                id={`LoadPortfoliosModal.${isComparing ? 'ComparingSelected' : 'CompareSelected'}`}
                values={{ count: selectedPortfolios.length }}
              />
            </PrimaryButton>
          </div>
        </Modal.Body>
      </Modal>
    );
  };

  renderModalV2 = (): JSX.Element => {
    const { selectedTab, selectedContact, user, queries, isComparing } = this.props;

    switch (selectedTab) {
      case LOAD_PORTFOLIOS_TABS.MODELS: {
        return <LoadModelsModal user={user} show onCancel={this.onClose} addPortfoliosToComparison={this.handleSelectedPortfolios} />;
      }
      case LOAD_PORTFOLIOS_TABS.CONTACTS: {
        return (
          <ContactPortfoliosModal
            isComparing={isComparing}
            query={queries.contactPortfolios}
            show
            selectedContact={selectedContact}
            onCancel={this.onClose}
            onSearchContactPortfolios={this.props.onSearchContactPortfolios}
            loadContactPortfolios={this.props.loadContactPortfolios}
            loadMoreContactPortfolios={this.props.onLoadMoreContactPortfolios}
            addPortfoliosToComparison={this.handleSelectedPortfolios}
            resetCurrentContact={this.props.resetCurrentContact}
            layout={getContactLayout(user)}
          />
        );
      }
      default: {
        return <></>;
      }
    }
  };

  render() {
    const { userPreferences } = this.props;
    const isThemeLight = userPreferences?.theme === ThemeMode.LIGHT;

    return isThemeLight ? this.renderModalV2() : this.renderModalV1();
  }
}

const mapStateToProps = (state: ReduxState): StateProps => ({
  contacts: state.contacts.contacts,
  nextContactsToken: state.contacts.nextContactsToken,
  loadingContacts: selectIsLoadingEvery(state, [GET_CONTACTS.type]),
  loadingContactPortfolios: state.contacts.loading.current.portfolios,
  userPreferences: state.auth.user?.preferences,
  isPostMessageActive: state.postMessage.isPostMessageActive,
});
export default connect(mapStateToProps, {
  getMyContacts,
  setCurrentContact,
  startLoaderAction,
  setIsActiveContactPostMessage,
  setIsActivePortfolioPostMessage,
  setPersistenceSource,
})(LoadPortfoliosModal);
